import React from "react";

import { DailySalesPoint } from "../../types/dashboard";

interface SalesChartProps {
  data: DailySalesPoint[];
}

const SalesChart: React.FC<SalesChartProps> = ({ data }) => {
  // Find the maximum value for scaling
  const maxSales = Math.max(...data.map((point) => point.total_sales));

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100); // Assuming amount is in cents
  };

  // Format date (e.g., "Jan 15")
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="w-full h-64">
      {/* Chart Container */}
      <div className="relative h-52">
        {/* Y-axis labels */}
        <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pr-2">
          <span>{formatCurrency(maxSales)}</span>
          <span>{formatCurrency(maxSales * 0.75)}</span>
          <span>{formatCurrency(maxSales * 0.5)}</span>
          <span>{formatCurrency(maxSales * 0.25)}</span>
          <span>{formatCurrency(0)}</span>
        </div>

        {/* Chart area */}
        <div className="ml-16 h-full flex items-end">
          <div className="relative w-full h-full">
            {/* Horizontal grid lines */}
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
              <div className="border-t border-gray-200 dark:border-gray-700 w-full h-0" />
              <div className="border-t border-gray-200 dark:border-gray-700 w-full h-0" />
              <div className="border-t border-gray-200 dark:border-gray-700 w-full h-0" />
              <div className="border-t border-gray-200 dark:border-gray-700 w-full h-0" />
              <div className="border-t border-gray-200 dark:border-gray-700 w-full h-0" />
            </div>

            {/* Bars */}
            <div className="absolute inset-0 flex items-end">
              <div className="w-full flex items-end justify-between">
                {data.map((point, index) => {
                  // Calculate height percentage
                  const percentage = (point.total_sales / maxSales) * 100;

                  return (
                    <div
                      key={index}
                      className="group flex flex-col items-center"
                      style={{ height: "100%", width: `${100 / data.length}%` }}
                    >
                      {/* Tooltip */}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full mb-2 bg-gray-800 dark:bg-gray-700 text-white text-xs rounded py-1 px-2 pointer-events-none">
                        <p className="font-semibold">
                          {formatCurrency(point.total_sales)}
                        </p>
                        <p className="text-gray-300">
                          {point.order_count} orders
                        </p>
                        <p className="text-gray-300">
                          {formatDate(point.date)}
                        </p>
                      </div>

                      {/* Bar */}
                      <div
                        className="w-4/5 bg-primary-500 hover:bg-primary-600 rounded-t transition-all"
                        style={{
                          height: `${Math.max(percentage, 2)}%`,
                          transition: "height 0.5s ease-out",
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="ml-16 mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
        {data.map((point, index) => (
          <div
            key={index}
            className="text-center"
            style={{ width: `${100 / data.length}%` }}
          >
            {formatDate(point.date)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SalesChart;
